import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, RefreshCw } from 'lucide-react';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { CustomsGate } from '../../nobel';
import { gamificationService } from '../../../services/gamificationService';
import { authService } from '../../../services/authService';

interface WordNode {
  id: string;
  word: string;
  definition: string;
  synonyms: string[];
  position: { x: number; y: number; z: number };
  connections: string[];
  category: 'academic' | 'formal' | 'colloquial';
}

const VocabularyMatrix3D: React.FC = () => {
  const userId = sessionStorage.getItem('ielts_user_id') || '';
  const { canAccess, isLoading, isDev, tier } = useFeatureAccess('advanced_module', userId);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const [nodes, setNodes] = useState<WordNode[]>([]);
  const [selectedNode, setSelectedNode] = useState<WordNode | null>(null);
  const [connections, setConnections] = useState<Array<[string, string]>>([]);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!isLoading) {
      if (!canAccess && !isDev) {
        setPaywallOpen(true);
      } else {
        generateNodes();
      }
    }
  }, [canAccess, isLoading, isDev]);

  const generateNodes = () => {
    const sampleWords = [
      { word: 'Ubiquitous', definition: 'Present everywhere', synonyms: ['pervasive', 'omnipresent', 'widespread'], cat: 'academic' },
      { word: 'Ameliorate', definition: 'Make better', synonyms: ['improve', 'enhance', 'upgrade'], cat: 'formal' },
      { word: 'Ephemeral', definition: 'Lasting very short', synonyms: ['transient', 'fleeting', 'temporary'], cat: 'academic' },
      { word: 'Pragmatic', definition: 'Practical approach', synonyms: ['realistic', 'sensible', 'practical'], cat: 'formal' },
      { word: 'Ambiguous', definition: 'Open to interpretation', synonyms: ['vague', 'unclear', 'uncertain'], cat: 'academic' },
      { word: 'Facilitate', definition: 'Make easier', synonyms: ['enable', 'assist', 'help'], cat: 'formal' },
      { word: 'Obsolete', definition: 'No longer used', synonyms: ['outdated', 'antiquated', 'archaic'], cat: 'formal' },
      { word: 'Resilient', definition: 'Able to recover', synonyms: ['adaptable', 'flexible', 'robust'], cat: 'academic' },
    ];

    const newNodes: WordNode[] = sampleWords.map((word, idx) => ({
      id: `node-${idx}`,
      word: word.word,
      definition: word.definition,
      synonyms: word.synonyms,
      category: word.cat as any,
      position: {
        x: (Math.random() - 0.5) * 600,
        y: (Math.random() - 0.5) * 400,
        z: (Math.random() - 0.5) * 200
      },
      connections: []
    }));

    setNodes(newNodes);
    setConnections([]);
    setScore(0);
    setCombo(0);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;

    const deltaX = e.clientX - lastMouse.current.x;
    const deltaY = e.clientY - lastMouse.current.y;

    setRotation(prev => ({
      x: prev.x + deltaY * 0.5,
      y: prev.y + deltaX * 0.5
    }));

    lastMouse.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleConnect = (nodeId: string) => {
    if (!selectedNode) {
      const node = nodes.find(n => n.id === nodeId);
      setSelectedNode(node || null);
      return;
    }

    if (selectedNode.id === nodeId) {
      setSelectedNode(null);
      return;
    }

    const targetNode = nodes.find(n => n.id === nodeId);
    if (!targetNode) return;

    // Check if connection is valid (synonyms or same category logic simulation)
    const isValid =
      selectedNode.synonyms.some(s => targetNode.synonyms.includes(s)) ||
      selectedNode.category === targetNode.category;

    if (isValid) {
      setConnections(prev => [...prev, [selectedNode.id, nodeId]]);
      const points = 100 * (combo + 1);
      setScore(prev => prev + points);
      setCombo(prev => prev + 1);

      if (userId) {
        gamificationService.awardXP(userId, combo >= 4 ? 'combo_5x' : 'question_correct');
      }
    } else {
      setCombo(0);
    }

    setSelectedNode(null);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'academic': return '#3b82f6';
      case 'formal': return '#C5A059';
      case 'colloquial': return '#10b981';
      default: return '#6b7280';
    }
  };

  return (
    <>
      <div className="h-full flex flex-col bg-[#0a0a0a] rounded-2xl overflow-hidden relative">
        {/* HUD */}
        <div className="absolute top-0 left-0 right-0 z-20 p-6 flex justify-between items-start pointer-events-none">
          <div className="bg-black/80 backdrop-blur-sm border border-white/10 rounded-xl p-4 pointer-events-auto">
            <div className="flex items-center gap-4">
              <div>
                <div className="text-[8px] mono text-stone-500 uppercase mb-1">Neural Score</div>
                <div className="text-2xl font-black text-[#C5A059]">{score}</div>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div>
                <div className="text-[8px] mono text-stone-500 uppercase mb-1">Combo</div>
                <div className="text-2xl font-black text-emerald-400">{combo}x</div>
              </div>
            </div>
          </div>

          <button
            onClick={generateNodes}
            className="bg-[#C5A059] text-stone-900 p-3 rounded-xl hover:scale-105 transition-transform pointer-events-auto"
          >
            <RefreshCw size={20} />
          </button>
        </div>

        {/* 3D Matrix Container */}
        <div
          ref={containerRef}
          className="flex-1 relative overflow-hidden cursor-grab active:cursor-grabbing"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{
            perspective: '1000px',
            transformStyle: 'preserve-3d'
          }}
        >
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{
              transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
              transformStyle: 'preserve-3d',
              transition: isDragging.current ? 'none' : 'transform 0.3s ease-out'
            }}
          >
            {/* Connection Lines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1, overflow: 'visible' }}>
              {connections.map(([from, to], idx) => {
                const fromNode = nodes.find(n => n.id === from);
                const toNode = nodes.find(n => n.id === to);
                if (!fromNode || !toNode) return null;

                return (
                  <motion.line
                    key={`${from}-${to}`}
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 0.6 }}
                    transition={{ duration: 0.5 }}
                    x1={`${50 + fromNode.position.x / 10}%`}
                    y1={`${50 + fromNode.position.y / 10}%`}
                    x2={`${50 + toNode.position.x / 10}%`}
                    y2={`${50 + toNode.position.y / 10}%`}
                    stroke="#C5A059"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                );
              })}
            </svg>

            {/* Word Nodes */}
            {nodes.map((node) => (
              <motion.div
                key={node.id}
                className="absolute"
                style={{
                  left: `${50 + node.position.x / 10}%`,
                  top: `${50 + node.position.y / 10}%`,
                  transform: `translate(-50%, -50%) translateZ(${node.position.z}px)`,
                  zIndex: 10 + Math.round(node.position.z)
                }}
                whileHover={{ scale: 1.2, zIndex: 100 }}
                onClick={() => handleConnect(node.id)}
              >
                <div
                  className={`relative group cursor-pointer`}
                  style={{
                    filter: `brightness(${1 + node.position.z / 200})`
                  }}
                >
                  <div
                    className="absolute inset-0 rounded-full blur-xl opacity-50"
                    style={{
                      backgroundColor: getCategoryColor(node.category),
                      transform: 'scale(1.5)'
                    }}
                  />

                  <div
                    className={`relative w-32 h-32 rounded-full flex items-center justify-center border-4 transition-all ${selectedNode?.id === node.id
                      ? 'border-[#C5A059] bg-[#C5A059]/20 ring-4 ring-[#C5A059]/50'
                      : 'border-white/30 bg-white/10 hover:border-white/60'
                      }`}
                    style={{
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <div className="text-center px-2 select-none">
                      <div className="text-white font-black text-sm mb-1">{node.word}</div>
                      <div className="text-[8px] mono text-stone-400 uppercase">{node.category}</div>
                    </div>

                    {selectedNode?.id === node.id && (
                      <motion.div
                        className="absolute -top-2 -right-2"
                        animate={{ rotate: 360, scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <Sparkles size={16} className="text-[#C5A059]" />
                      </motion.div>
                    )}
                  </div>

                  <div className="absolute top-full mt-4 left-1/2 -translate-x-1/2 w-48 bg-black/90 border border-white/20 rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none backdrop-blur-sm z-50">
                    <div className="text-white font-bold text-xs mb-2">{node.definition}</div>
                    <div className="flex flex-wrap gap-1">
                      {node.synonyms.map((syn, idx) => (
                        <span key={idx} className="text-[8px] mono bg-white/10 px-2 py-1 rounded text-stone-300">
                          {syn}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Instructions */}
        <div className="absolute bottom-0 left-0 right-0 p-6 z-20 pointer-events-none">
          <div className="bg-black/80 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center pointer-events-auto inline-block mx-auto">
            <p className="text-stone-400 text-sm mono">
              <strong className="text-[#C5A059]">DRAG</strong> to rotate • <strong className="text-[#C5A059]">CLICK</strong> nodes to connect related terms
            </p>
          </div>
        </div>
      </div>

      <CustomsGate
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        feature="VOCABULARY MATRIX 3D"
      />
    </>
  );
};

export default VocabularyMatrix3D;